import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Radio, Tv, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { fmtTime } from "@/lib/format";

export default function MatchesList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [umpires, setUmpires] = useState([]);
  const [form, setForm] = useState({ team_a_id: "", team_b_id: "", stage: "group", court: "Court 1" });
  const canCreate = ["super_admin", "league_admin"].includes(user.role);
  const canAssign = canCreate;

  const load = async () => {
    const [m, t] = await Promise.all([api.get("/matches"), api.get("/teams")]);
    setMatches(m.data);
    setTeams(t.data);
    if (canAssign) {
      try {
        const { data } = await api.get("/users");
        setUmpires((data || []).filter(u => u.role === "court_umpire"));
      } catch { /* silent */ }
    }
  };
  useEffect(() => { load(); }, []);

  const assignReferee = async (matchId, userId) => {
    try {
      await api.post(`/matches/${matchId}/assign-referee`, { user_id: userId || null });
      toast.success(userId ? "Referee assigned" : "Referee cleared");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const create = async () => {
    if (!form.team_a_id || !form.team_b_id || form.team_a_id === form.team_b_id) {
      return toast.error("Choose two different teams");
    }
    try {
      await api.post("/matches", form);
      toast.success("Match created");
      setForm({ team_a_id: "", team_b_id: "", stage: "group", court: "Court 1" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this match?")) return;
    await api.delete(`/matches/${id}`);
    load();
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Module 1 · Live Scoring</div>
        <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white">Matches</h1>
      </div>

      {canCreate && (
        <Card className="p-6 bg-[#11241b] border-white/10 mb-6">
          <div className="font-anton text-xl uppercase tracking-tight text-white mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#00A86B]" /> Create Match
          </div>
          <div className="grid md:grid-cols-5 gap-3 items-end">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Team A</label>
              <Select value={form.team_a_id} onValueChange={(v) => setForm({ ...form, team_a_id: v })}>
                <SelectTrigger data-testid="select-team-a" className="bg-[#060d09] border-white/10 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[#11241b] border-white/10 text-white">
                  {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Team B</label>
              <Select value={form.team_b_id} onValueChange={(v) => setForm({ ...form, team_b_id: v })}>
                <SelectTrigger data-testid="select-team-b" className="bg-[#060d09] border-white/10 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[#11241b] border-white/10 text-white">
                  {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Stage</label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger data-testid="select-stage" className="bg-[#060d09] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#11241b] border-white/10 text-white">
                  <SelectItem value="group">Group</SelectItem>
                  <SelectItem value="quarter">Quarter-Final</SelectItem>
                  <SelectItem value="semi">Semi-Final</SelectItem>
                  <SelectItem value="final">Final</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Court</label>
              <Input data-testid="input-court" value={form.court} onChange={(e) => setForm({ ...form, court: e.target.value })} className="bg-[#060d09] border-white/10 text-white mt-1" />
            </div>
            <Button data-testid="btn-create-match" onClick={create} className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold">Create</Button>
          </div>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {matches.map((m) => (
          <Card key={m.id} className="p-5 bg-[#11241b] border-white/10" data-testid={`match-card-${m.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">{m.court} · {m.stage}</span>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${m.status === "live" ? "bg-red-500 text-white" : m.status === "finished" ? "bg-zinc-700 text-white" : "bg-zinc-800 text-zinc-400"}`}>
                {m.status === "live" && <span className="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse mr-1.5" />}
                {m.status}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-3 mb-3">
              <div className="font-anton text-2xl uppercase text-white truncate">{m.team_a?.name || "—"}</div>
              <div className="font-mono-score text-3xl text-[#00A86B]">{m.score_a} : {m.score_b}</div>
              <div className="font-anton text-2xl uppercase text-white text-right truncate">{m.team_b?.name || "—"}</div>
            </div>
            <div className="text-xs text-zinc-500 mb-3">
              Match clock: {fmtTime(m.match_remaining)} · Period {m.current_period}
              {m.format_type === "set_based" && <span className="ml-2 text-[#F4B400]">· {m.format_config?.sets_per_match || 2}×{m.format_config?.games_per_set || 6}</span>}
            </div>
            {canAssign && (
              <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid={`ref-assign-${m.id}`}>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">Referee:</span>
                <Select
                  value={m.assigned_referee_id || ""}
                  onValueChange={(v) => assignReferee(m.id, v === "__none__" ? null : v)}
                >
                  <SelectTrigger className="h-7 w-56 bg-[#060d09] border-white/10 text-xs" data-testid={`ref-select-${m.id}`}>
                    <SelectValue placeholder="Unassigned — any umpire" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#11241b] border-white/10 text-white">
                    <SelectItem value="__none__">— Unassigned —</SelectItem>
                    {umpires.map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
                {m.assigned_referee_id && (
                  <span className="text-[10px] text-emerald-300">Locked to assignee</span>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {["super_admin", "league_admin", "court_umpire"].includes(user.role) && (
                <Button data-testid={`btn-umpire-${m.id}`} size="sm" className="bg-[#00A86B] hover:bg-[#00935A] text-white" onClick={() => nav(m.format_type === "set_based" ? `/scoring/${m.id}` : `/umpire/${m.id}`)}>
                  <Radio className="w-3.5 h-3.5 mr-1.5" /> {m.format_type === "set_based" ? "Score Sets" : "Umpire Panel"}
                </Button>
              )}
              {m.tie_id && (
                <Button data-testid={`btn-tie-${m.id}`} size="sm" variant="outline" className="border-[#F4B400]/40 text-[#F4B400]" onClick={() => nav(`/tie/${m.tie_id}`)}>
                  Team Tie
                </Button>
              )}
              <Button data-testid={`btn-view-${m.id}`} size="sm" variant="outline" className="border-white/20 text-white" onClick={() => nav(`/spectator/${m.id}`)}>
                <Tv className="w-3.5 h-3.5 mr-1.5" /> Broadcast
              </Button>
              {["super_admin", "league_admin"].includes(user.role) && m.status !== "finished" && (
                <Button data-testid={`btn-finalize-${m.id}`} size="sm" variant="outline" className="border-amber-500/40 text-amber-400" onClick={async () => {
                  if (!window.confirm("Force-finalize this match? Winner will be computed and bracket auto-advance will fire.")) return;
                  try { await api.post(`/matches/${m.id}/finalize`); toast.success("Finalized"); load(); }
                  catch (e) { toast.error(formatApiError(e)); }
                }}>Finalize</Button>
              )}
              {["super_admin", "league_admin"].includes(user.role) && (
                <Button data-testid={`btn-edit-match-${m.id}`} size="sm" variant="outline" className="border-white/10 text-white" onClick={async () => {
                  const court = window.prompt("Court name:", m.court);
                  if (court == null) return;
                  const scheduled = window.prompt("Scheduled at (ISO, e.g. 2026-09-11T14:30:00 or blank):", m.scheduled_at || "");
                  if (scheduled == null) return;
                  try { await api.patch(`/matches/${m.id}`, { court, scheduled_at: scheduled || null }); toast.success("Updated"); load(); }
                  catch (e) { toast.error(formatApiError(e)); }
                }}>Edit Court/Time</Button>
              )}
              {user.role === "super_admin" && (
                <Button data-testid={`btn-delete-match-${m.id}`} size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10" onClick={() => del(m.id)}>
                  Delete
                </Button>
              )}
            </div>
          </Card>
        ))}
        {matches.length === 0 && (
          <div
            className="text-zinc-500 col-span-2 text-center py-12"
            data-testid="matches-empty"
          >
            {user.role === "court_umpire"
              ? "No matches assigned to you yet. Ask an admin to assign you as referee."
              : "No matches yet."}
          </div>
        )}
      </div>
    </div>
  );
}
